<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { useConfigStore, useLoadingStore, useToastStore } from '@/stores'
import { useLocalAdFree } from '@/composables'
import { AD_FINISH_ACTION, STORAGE_CUSTOM_THEME } from '@/constants'
import { KS } from '@/kuaishou'
import { $log } from '@/helpers'
import { track } from '@/track'

const props = withDefaults(defineProps<{
  value: boolean
  detailSessionId: string
  caption: string
  coverUrl: string
  h5Data: any
  contentId: string
  chapterId: string
}>(), {
  h5Data: {},
})

const emit = defineEmits<{
  (event: 'input', value: boolean): void
  (event: 'end'): void
  (event: 'error'): void
}>()

const theme = useLocalStorage(STORAGE_CUSTOM_THEME, {
  background: '#FFF',
  size: 'md' as 'sm' | 'md' | 'lg' | 'xl',
})

const configStore = useConfigStore()
const { $showLoading, $hideLoading } = useLoadingStore()
const { $toast } = useToastStore()

const imagePlayer = ref(true)
const countdowning = ref(true)
const autoNext = computed(() => {
  return configStore.$state.playBackStatus === AD_FINISH_ACTION.NEXT
})
function onCountdownEnd() {
  countdowning.value = false
  if (!autoNext.value)
    return

  // Note: 倒计时结束自动进入下一页
  emit('input', false)
}

onMounted(() => {
  KS.adImageTrack(props.detailSessionId)
})
onUnmounted(() => {
  KS.adImageDestroy(props.detailSessionId)
})

function openAdDetail() {
  KS.openAdImageDetail(props.detailSessionId)
}

const adVideoPlayer = ref(false)
const adVideoData = ref<any>({})
const { setEnableMinutes } = useLocalAdFree()
async function onLoadAdVideoForFree() {
  $log('Component: BizAdImagePlayer', '#onLoadAdVideoForFree')
  $showLoading()
  try {
    track('inspireVideo', { name: 'freeAd', type: 'start' })
    await KS.playAdVideo({
      type: 1,
      success(data: any) {
        track('inspireVideo', { name: 'freeAd', type: 'end' })
        $log('Component: BizAdImagePlayer', '#onLoadAdVideoForFree ~> success', { data })
        $toast(`看完广告视频, 可免 ${configStore.$state.sourceFreeTime} 分钟广告`)
        setEnableMinutes(configStore.$state.freeTime)
        emit('input', false)
      },
      fail(error: any) {
        track('inspireVideo', { name: 'freeAd', type: 'cancel' })
        $log('Component: BizAdImagePlayer', '#onLoadAdVideoForFree ~> fail', { error })
        $toast('未看完视频，无法领取奖励')
        emit('input', false)
      },
    })
    // imagePlayer.value = false
  }
  catch {}
  finally {
    $hideLoading()
  }
}

function onEndAdVideo() {
  adVideoPlayer.value = false
  setEnableMinutes(configStore.$state.freeTime)
  $toast(`看完广告视频, 可免 ${configStore.$state.sourceFreeTime} 分钟广告`, 3000)
  emit('input', false)
}
</script>

<template>
  <div v-if="value">
    <div
      v-if="imagePlayer" class="px-10 flex flex-col justify-center fixed inset-0 z-100"
      :style="{ background: theme.background }"
    >
      <p class="flex items-center justify-center text-17px text-#333">
        <span>广告是为了更好的支持正版</span>
      </p>
      <div class="mt-6.5 w-260px mx-a relative rounded-lg overflow-hidden">
        <span class="text-white absolute top-2 left-2 px-2 py-0.5 text-xs rounded-tl-2 rounded-br-2 bg-#00000060">广告</span>
        <img :src="coverUrl" class="rounded aspect-9/16">
        <div class="w-full p-4 absolute bottom-0 bg-gradient-to-b from-transparent to-#000000cc">
          <p v-if="caption" class="w-full text-white">
            {{ caption }}
          </p>
          <div class="w-full mt-4">
            <BizTrackClick
              name="insertAd" type="button" :extra="{ contentId, chapterId, sessionId: detailSessionId, type: 'image' }"
              class="block w-full py-2 font-bold text-lg text-center text-white rounded-full bg-primary"
              @click="openAdDetail"
            >
              查看详情
            </BizTrackClick>
          </div>
        </div>
      </div>

      <p v-if="configStore.$state.freeTime" class="mt-4 flex items-center justify-center text-primary text-center" @click="onLoadAdVideoForFree">
        <span>看视频免{{ configStore.$state.sourceFreeTime }}分钟广告</span>
        <i-icon-park-outline-right class="mt-0.5" />
      </p>

      <div class="fixed w-full left-0 bottom-8 text-center text-sm text-#999">
        <p v-if="countdowning">
          <BasicCountdown :seconds="configStore.$state.pageDwellTime" @end="onCountdownEnd">
            <template #default="{ seconds }">
              {{ autoNext ? `${seconds} 秒后自动进入下一页` : `${seconds} 秒后可关闭` }}
            </template>
          </BasicCountdown>
        </p>
        <BizTrackClick
          v-else name="insertAdClose" type="button"
          :extra="{ contentId, chapterId, sessionId: detailSessionId, type: 'image' }"
          @click="$emit('input', false)"
        >
          继续阅读下一章
        </BizTrackClick>
      </div>
    </div>

    <!-- <BizAdVideoPlayerForFree
      v-if="adVideoPlayer"
      v-model="adVideoPlayer" :session-id="adVideoData.sessionId"
      :src="adVideoData.src" :poster="adVideoData.poster" :caption="adVideoData.caption"
      :is-download="adVideoData.isDownload" :action-bar="adVideoData.actionBar" :action-bar-color="adVideoData.actionBarColor"
      @end="onEndAdVideo"
    /> -->
  </div>
</template>
